// Angular
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

// Third party
import { Observable } from 'rxjs';

// Local library
import { API } from '@amwager/util-ts';

// Local file
import { BASE_URL, URL_API_REWARD } from '../tokens';


/**
 * Thin HttpClient wrapper for standard REST calls (GET/POST/PUT/DELETE/request)
 */
@Injectable()
export class HttpService {

  private url!: string;

  /**
   * @description
   * Private variable to store the API URL
   */
  private urlReward!: string;

  /**
   * @description
   * Private variable to store the API URL
   */
  private urlPromo!: string;

  /**
   * @description
   * Getter for the API URL
   * @returns The current API URL
   */
  get apiUrl(): string { return this.url; }

  /**
   * @description
   * Setter for the API URL
   * @param url The URL to set
   */
  set apiUrl(url: string) { this.url = url; }

  /**
   * Base API URL
   * @returns {string} Current API URL
   */
  get apiRewardUrl(): string { return this.urlReward; }

  /**
   * Set the base API URL
   * @param {string} url URL to set
   */
  set apiRewardUrl(url: string) { this.urlReward = url; }
  /**
   * @description
   * Getter for the API URL
   * @returns The current API URL
   */
  get apiPromoUrl(): string { return this.urlPromo; }

  /**
   * @description
   * Setter for the API URL
   * @param url The URL to set
   */
  set apiPromoUrl(url: string) { this.urlPromo = url; }

  constructor(
    private readonly http: HttpClient,
    @Inject(BASE_URL) private readonly api: string,
    @Inject(URL_API_REWARD) private readonly apiReward: string,
  ) {
    this.apiUrl = this.api;
    this.apiRewardUrl = this.apiReward;
  }

  /**
   * @description
   * Performs HTTP GET reward requests to the API
   *
   * @param data Configuration object containing URL and params
   * @returns Observable that emits the response body as type T
   * @template T The type of the response
   */
  getReward$<T>(data: API.IHttpParams): Observable<T> {
    return this.http.get<T>(
      this.getApiUrl(data),
      { params: data.params }
    );
  }

  /**
   * GET request
   * @param {API.IHttpParams} data URL and params config
   * @returns {Observable<T>} Response body
   */
  get$<T>(data: API.IHttpParams): Observable<T> {
    return this.http.get<T>(
      this.getApiUrl(data),
      { params: data.params }
    );
  }

  /**
   * GET request that keeps the body as raw text
   * @param {API.IHttpParams} data URL and params config
   * @returns {Observable<string>} Unparsed response body
   */
  getText$(data: API.IHttpParams): Observable<string> {
    return this.http.get(
      this.getApiUrl(data),
      { params: data.params, responseType: 'text' }
    );
  }

  /**
   * POST request
   * @param {API.IHttpParams} data URL, body config
   * @returns {Observable<T>} Response body
   */
  post$<T>(data: API.IHttpParams): Observable<T> {
    return this.http.post<T>(
      this.getApiUrl(data),
      data.body
    );
  }

  /**
   * PUT request
   * @param {API.IHttpParams} data URL, body config
   * @returns {Observable<T>} Response body
   */
  put$<T>(data: API.IHttpParams): Observable<T> {
    return this.http.put<T>(
      this.getApiUrl(data),
      data.body
    );
  }

  /**
   * DELETE request
   * @param {API.IHttpParams} data URL and params config
   * @returns {Observable<T>} Response body
   */
  delete$<T>(data: API.IHttpParams): Observable<T> {
    return this.http.delete<T>(
      this.getApiUrl(data),
      { params: data.params }
    );
  }

  /**
   * Dynamic-method HTTP request
   * @param {API.IHttpParams} data method, URL, body, params
   * @returns {Observable<T>} Response body
   */
  request$<T>(data: API.IHttpParams): Observable<T> {
    return this.http.request<T>(
      data.method as string,
      this.getApiUrl(data),
      {
        body: data.body || '',
        params: data.params || ''
      }
    );
  }

  /**
   * Resolve the full URL (data.url > data.apiUrl+api > this.apiUrl+api)
   * @param {API.IHttpParams} data URL config
   * @returns {string} Fully-qualified URL
   */
  getApiUrl(data: API.IHttpParams): string {
    return data.url
      ? data.url
      : data.apiUrl
        ? data.apiUrl.concat(data.api ?? '')
        : data.api?.includes('GetActiveGiveawaysAndPromosForUsers') || data.api?.includes('OptIn')
          ? this.apiRewardUrl.concat(data.api ?? '')
          : this.apiUrl.concat(data.api ?? '')
  }

  /**
   * Build HttpParams from an object (skips undefined values)
   * @param {Record<string, unknown>} obj Source object
   * @returns {HttpParams | null} Built HttpParams, or null on error
   */
  objToHttpParams(obj: Record<string, unknown>): HttpParams | null {
    let params: HttpParams = new HttpParams();
    try {
      Object.entries(obj).forEach(
        ([key, value]): void => {
          if (key && value!==undefined) params = params.append(
            String(key),
            String(value) || value as string
          )
        }
      );
    }
    catch(e) {
      console.error(`@toHttpParams:${e}`);
      return null;
    }

    return params;
  }
}
